import 'core-js/stable';

import haversine from 'haversine';
import moment from 'moment-timezone';
import { react2angular } from 'react2angular';

import {
  Analytics,
  AffiliatedRestaurants,
  Badge,
  BadgeCollection,
  BadgeResySelect,
  GdaPromotional,
  Icon,
  IconBadge,
  IconLoader,
  IconLogo,
  IconFooterLogo,
  ImageCarousel,
  Loader,
  PartnershipBranding,
  ProfilePhoto,
  Rating,
  ReservationButtonList,
  Rga,
  SafetyMeasures,
  StripeForm,
  UpcomingEventsContainer,
  utils,
  VenueAnnouncement,
  VenueLocationSummary,
} from '@resy/ui-kitchen';

import DeltaWifi from './components/DeltaWifi/WrappedDeltaWifi';
import DeltaWifiLocation from './components/DeltaWifi/DeltaWifiLocation/WrappedDeltaWifiLocation';
import DeltaWifiMenuContainer from './components/DeltaWifi/DeltaWifiMenuContainer/WrappedDeltaWifiMenuContainer';
import AuthContainer from './containers/AuthContainer';
import CalendarCloseButton from './components/CalendarCloseButton/CalendarCloseButton';
import BlendError from './components/BlendError';
import ErrorView from './components/ErrorView';
import MaintenancePageContainer from './containers/MaintenancePageContainer';
import InstallRedirect from './components/InstallRedirect';
import { Selector } from './components/Nav';
import Menu from './components/Menu';
import ResyRouter from './components/ResyRouter';
import Ribbon from './components/Ribbon';
import SurveyContainer from './containers/SurveyContainer/SurveyContainer';
import IconGreenCheck from '../../../ui-kitchen/src/components/IconGreenCheck';
import '../../../../node_modules/normalize.css/normalize.css';
import '../../../ui-kitchen/src/style/font-definitions.scss';
import {
  AccountReservationsProvider,
  AutocompleteContainerProvider,
  AlgorithmicListsProvider,
  DownloadProvider,
  EventDetailsContainerProvider,
  EventsByLocationProvider,
  FooterProvider,
  AmexAdsContainerProvider,
  HeaderProvider,
  ListVenuesProvider,
  LocationsContainerProvider,
  MenuContainerProvider,
  ModalsContainerProvider,
  NotifyChefsListModalProvider,
  NotifyProvider,
  NotifyVenueGroupModalProvider,
  OptOutProvider,
  PasswordResetProvider,
  PersonalizedListProvider,
  PrepayConfirmationProvider,
  PrepayPaymentProvider,
  PrepayStartProvider,
  RatingFormProvider,
  ResyCalendarProvider,
  ResySpotlightProvider,
  HomepageCollectionsNavProvider,
  RGAListContainerProvider,
  RSVPProvider,
  SearchContainerProvider,
  TopNavProvider,
  VenueListContainerProvider,
  VenuePageProvider,
  SidenavProvider,
} from './components/ReduxProviders';
import Logger from '../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../ui-kitchen/src/utils/sanitizeString';

// to support search
window.haversine = haversine;

// eslint-disable-next-line no-underscore-dangle, no-undef
window.__DESCRIPTION__ = __DESCRIPTION__;
// eslint-disable-next-line no-underscore-dangle, no-undef
window.__VERSION__ = __VERSION__;
// eslint-disable-next-line no-underscore-dangle, no-undef
window.__SHA__ = __SHA__;
// eslint-disable-next-line no-underscore-dangle, no-undef
window.__REFERER__ = window.document.referrer;
// eslint-disable-next-line no-underscore-dangle, no-undef
window.__ENCODEDREFERER__ = encodeURIComponent(window.document.referrer);

IconLoader((name) => {
  const sanitizedName = sanitizeString(name);
  return import(
    /* webpackChunkName: "[request]" */
    `./svg/dynamic/${sanitizedName}.svg`
  )
    .then(
      (module) => module.default,
      () => Logger.error(`[ERROR] Icon ${sanitizedName} does not exist`)
    );
});

const { language } = navigator;
if (language.includes('en')) {
  moment.updateLocale('en', {
    calendar: {
      sameDay: '[Today]',
      nextDay: '[Tomorrow]',
      nextWeek: 'ddd., MMM. D',
      sameElse: 'ddd., MMM. D',
    },
  });
} else if (language.includes('fr')) {
  moment.updateLocale('fr', {
    calendar: {
      sameDay: '[Aujourd’hui]',
      nextDay: '[Demain]',
      nextWeek: 'ddd D MMM',
      sameElse: 'ddd D MMM',
    },
  });
} else if (language.includes('es')) {
  moment.updateLocale('es', {
    calendar: {
      sameDay: '[hoy]',
      nextDay: '[mañana]',
      nextWeek: 'ddd, D MMM',
      sameElse: 'ddd, D MMM',
    },
  });
} else if (language.includes('ca')) {
  moment.updateLocale('ca', {
    calendar: {
      sameDay: '[avui]',
      nextDay: '[demà]',
      nextWeek: 'ddd D MMM',
      sameElse: 'ddd D MMM',
    },
  });
  // Fallback to en
} else {
  moment.updateLocale('en', {
    calendar: {
      sameDay: '[Today]',
      nextDay: '[Tomorrow]',
      nextWeek: 'ddd., MMM. D',
      sameElse: 'ddd., MMM. D',
    },
  });
}

function CalendarStateDeterminer() {
  return { determine: utils.calendarStateDeterminer };
}

function PageContext() {
  return { get: utils.getPageContext };
}

angular
  .module('resyApp')
  .service('CalendarStateDeterminer', CalendarStateDeterminer)
  .service('PageContext', PageContext)
  .service('Analytics', Analytics)
  .component('calendar', react2angular(ResyCalendarProvider, null, ['$rootScope', '$routeParams', 'Analytics']))
  .component('ratingForm', react2angular(RatingFormProvider, null, ['Analytics', '$rootScope']))
  .component('ribbon', react2angular(Ribbon, null, ['Analytics']))
  .component('resyAccountReservations', react2angular(AccountReservationsProvider, null, ['$location', '$rootScope', '$route', 'Analytics']))
  .component('resyDeltaWifi', react2angular(DeltaWifi, null, ['$location', 'ResyAmexConfig', '$rootScope', 'Analytics']))
  .component('resyDeltaWifiLocation', react2angular(DeltaWifiLocation, null, ['$rootScope']))
  .component('resyDeltaIconClose', react2angular(CalendarCloseButton))
  .component('resyDeltaIconLogo', react2angular(IconLogo))
  .component('resyDeltaMenuContainer', react2angular(DeltaWifiMenuContainer, null, ['$rootScope', '$location', '$route', 'Analytics', 'AutomationMode']))
  .component('resyAffiliatedRestaurants', react2angular(AffiliatedRestaurants, null, ['Analytics']))
  .component('resyAuth', react2angular(AuthContainer, ['close'], ['$rootScope', '$route', '$location', 'Analytics', 'AutomationMode']))
  .component('resyAutocomplete', react2angular(AutocompleteContainerProvider, null, ['$rootScope', 'Analytics']))
  .component('resyBadge', react2angular(Badge))
  .component('resyBadgeCollection', react2angular(BadgeCollection))
  .component('resyConciergeReservations', react2angular(AccountReservationsProvider, null, ['$location', '$rootScope', '$route', 'Analytics']))
  .component('resySelectBadge', react2angular(BadgeResySelect))
  .component('resyDownload', react2angular(DownloadProvider, [], ['$rootScope', '$translate']))
  .component('resyBlendError', react2angular(BlendError, [], ['Analytics', '$rootScope']))
  .component('resyError', react2angular(ErrorView, [], ['Analytics', '$rootScope', '$translate']))
  .component('resyMaintenance', react2angular(MaintenancePageContainer, [], ['Analytics', '$rootScope', '$translate']))
  .component('resySpotlight', react2angular(ResySpotlightProvider, null, ['$rootScope', 'Analytics']))
  .component('installRedirect', react2angular(InstallRedirect, null, ['$location']))
  .component('resyListVenues', react2angular(ListVenuesProvider, null, ['$rootScope']))
  .component('resyLocationsContainer', react2angular(LocationsContainerProvider, null, ['$rootScope']))
  .component('resyMenu', react2angular(Menu))
  .component('resyMenuContainer', react2angular(MenuContainerProvider, null, ['$rootScope', '$location', '$route', 'Analytics', 'AutomationMode']))
  .component('resyModalsContainer', react2angular(ModalsContainerProvider, null, ['$rootScope', '$scope', '$window', '$location', '$route', 'Analytics', 'AutomationMode', 'ResyAmexConfig']))
  .component('resyPartnershipBranding', react2angular(PartnershipBranding))
  .component('resyPasswordReset', react2angular(PasswordResetProvider, null, ['$rootScope', '$routeParams', 'Analytics']))
  .component('resyProfilePhoto', react2angular(ProfilePhoto))
  .component('prepayPayment', react2angular(PrepayPaymentProvider, null, ['$location', '$rootScope', '$routeParams', 'AutomationMode', 'Prepay']))
  .component('prepayStart', react2angular(PrepayStartProvider, null, ['$location', '$rootScope', '$routeParams', 'Prepay']))
  .component('prepayConfirmation', react2angular(PrepayConfirmationProvider, null, ['$rootScope', '$routeParams', 'Prepay']))
  .component('resyRating', react2angular(Rating))
  .component('resyGdaPromotional', react2angular(GdaPromotional, null, ['Analytics', '$rootScope', '$location']))
  .component('resySearchContainer', react2angular(SearchContainerProvider, null, ['Analytics', '$rootScope', '$window']))
  .component('resySelector', react2angular(Selector))
  .component('ratingSurvey', react2angular(SurveyContainer, null, []))
  .component('resyTopNav', react2angular(TopNavProvider, null, ['Analytics', '$rootScope']))
  .component('resyVenueReservationButtonList', react2angular(ReservationButtonList)) // change name once resyReservationButtonList is replaced in inline-booking
  .component('resyIcon', react2angular(Icon))
  .component('resyIconBadge', react2angular(IconBadge))
  .component('resyIconClose', react2angular(CalendarCloseButton))
  .component('resyIconGreenCheck', react2angular(IconGreenCheck))
  .component('resyIconLogo', react2angular(IconLogo))
  .component('resyIconFooterLogo', react2angular(IconFooterLogo))
  .component('resyInviteRsvp', react2angular(RSVPProvider, null, ['$routeParams', '$rootScope', '$translate', '$route', '$location', 'Analytics', 'AutomationMode']))
  .component('resyLoader', react2angular(Loader))
  .component('algorithmicLists', react2angular(AlgorithmicListsProvider, null, ['$rootScope', 'Analytics']))
  .component('eventsByLocation', react2angular(EventsByLocationProvider, null, ['$rootScope', 'Analytics']))
  .component('eventDetailsContainer', react2angular(EventDetailsContainerProvider, null, ['$rootScope', '$routeParams', 'Analytics']))
  .component('homeHeader', react2angular(HeaderProvider, null, ['Analytics', '$rootScope', '$window']))
  .component('resyNotify', react2angular(NotifyProvider, null, ['$rootScope', '$route', '$scope', '$window', 'Analytics']))
  .component('imageCarousel', react2angular(ImageCarousel))
  .component('notifyVenueGroupModal', react2angular(NotifyVenueGroupModalProvider, null, ['$rootScope', 'Analytics']))
  .component('notifyChefsListModal', react2angular(NotifyChefsListModalProvider, null, ['$rootScope', 'Analytics']))
  .component('optOut', react2angular(OptOutProvider, null, ['$location', '$rootScope']))
  .component('personalizedList', react2angular(PersonalizedListProvider, null, ['Analytics', '$location', '$rootScope']))
  .component('rga', react2angular(Rga))
  .component('safetyMeasures', react2angular(SafetyMeasures))
  .component('sidenav', react2angular(SidenavProvider, null, ['Analytics', '$location', '$rootScope']))
  .component('stripeForm', react2angular(StripeForm, null, []))
  .component('venueListContainer', react2angular(VenueListContainerProvider, null, ['Analytics', '$location', '$rootScope']))
  .component('amexAdsContainer', react2angular(AmexAdsContainerProvider, ['page'], ['Analytics', '$location', '$rootScope']))
  .component('venuePage', react2angular(VenuePageProvider, null, ['Analytics', '$rootScope', '$routeParams', '$translate', 'ImageServiceConfig']))
  .component('homepageCollectionsNav', react2angular(HomepageCollectionsNavProvider, null, ['Analytics', '$rootScope']))
  .component('rgaListContainer', react2angular(RGAListContainerProvider, null, ['Analytics', '$rootScope', 'ResyEvents']))
  .component('venueLocationSummary', react2angular(VenueLocationSummary, null, ['Analytics']))
  .component('venueAnnouncement', react2angular(VenueAnnouncement, null, ['Analytics']))
  .component('upcomingEventsContainer', react2angular(UpcomingEventsContainer))
  .component('resyFooter', react2angular(FooterProvider, null, ['Analytics', '$rootScope', '$location']))
  .component('resyRouter', react2angular(ResyRouter, null, [
    '$location', '$rootScope', '$route', '$routeParams', '$translate', '$window', 'Analytics', 'AutomationMode', 'Config', 'ResyAmexConfig',
  ]));
